import React, { useEffect, useMemo, useState } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import vendedorService, { Vendedor } from '../services/vendedorService';
import {
  filterVendedoresByTerm,
  formatVendedorLabel,
  normalizeNroVendedor,
  sortVendedoresByName,
  VendedorLike,
} from '../utils/vendedorUtils';
import { SearchableObjectSelect } from './SearchableSelect';

export interface VendedorSelectProps {
  value?: number | null;
  onChange: (nroVendedor: number | null, vendedor?: VendedorLike | null) => void;
  label?: string;
  placeholder?: string;
  allowEmpty?: boolean;
  emptyOptionLabel?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  vendedores?: VendedorLike[];
  loading?: boolean;
  error?: boolean;
  helperText?: string;
  margin?: 'none' | 'dense' | 'normal';
  nombreVendedorFallback?: string | null;
}

const VendedorSelect: React.FC<VendedorSelectProps> = ({
  value = null,
  onChange,
  label = 'Vendedor',
  placeholder,
  allowEmpty = false,
  emptyOptionLabel = 'Todos',
  disabled = false,
  required = false,
  fullWidth = true,
  size = 'medium',
  sx,
  vendedores,
  loading: loadingProp,
  error = false,
  helperText,
  margin = 'none',
  nombreVendedorFallback,
}) => {
  const [internalVendedores, setInternalVendedores] = useState<Vendedor[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);

  const useExternalList = vendedores != null;
  const loading = useExternalList ? Boolean(loadingProp) : internalLoading;

  useEffect(() => {
    if (useExternalList) return undefined;

    let cancelled = false;

    (async () => {
      setInternalLoading(true);
      try {
        const data = await vendedorService.getVendedoresPaged({
          pageNumber: 1,
          pageSize: 500,
        });
        if (!cancelled) {
          setInternalVendedores(data.items ?? []);
        }
      } catch {
        if (!cancelled) {
          setInternalVendedores([]);
        }
      } finally {
        if (!cancelled) {
          setInternalLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [useExternalList]);

  const options = useMemo(() => {
    const base = sortVendedoresByName(useExternalList ? vendedores : internalVendedores);
    const nro = normalizeNroVendedor(value);

    if (nro != null && !base.some((v) => v.nroVendedor === nro)) {
      return [
        {
          nroVendedor: nro,
          nombreVendedor: (nombreVendedorFallback ?? '').trim() || `Vendedor ${nro}`,
        } satisfies VendedorLike,
        ...base,
      ];
    }

    return base;
  }, [useExternalList, vendedores, internalVendedores, value, nombreVendedorFallback]);

  const resolvedPlaceholder =
    placeholder ?? (allowEmpty ? emptyOptionLabel : 'Buscar por número o nombre...');

  return (
    <SearchableObjectSelect
      fullWidth={fullWidth}
      size={size}
      disabled={disabled}
      loading={loading}
      options={options}
      value={normalizeNroVendedor(value)}
      onChange={(nroVendedor, vendedor) => onChange(nroVendedor, vendedor)}
      getOptionValue={(option) => option.nroVendedor}
      getOptionLabel={(option) => formatVendedorLabel(option)}
      isOptionEqualToValue={(option, current) => option.nroVendedor === current}
      filterOptions={(opts, inputValue) => filterVendedoresByTerm(opts, inputValue)}
      allowEmpty={allowEmpty}
      required={required}
      label={label}
      placeholder={resolvedPlaceholder}
      error={error}
      helperText={helperText}
      margin={margin}
      sx={sx}
    />
  );
};

export default VendedorSelect;
